import React, { useState, useRef, useCallback, useEffect } from "react";
import { API_URL } from "../config";
import "./Wardrobe.css";

function getToken() {
  return localStorage.getItem("token") || "";
}

/* --- ImageModal --- */
function ImageModal({ src, label, onClose }) {
  if (!src) return null;
  return (
    <div className="cs-modal-overlay" onClick={onClose}>
      <div className="cs-modal" onClick={(e) => e.stopPropagation()}>
        <button className="cs-modal__close" onClick={onClose}>&times;</button>
        <img src={src} alt={label} className="cs-modal__image" />
        <span className="cs-modal__label">{label}</span>
      </div>
    </div>
  );
}

/* --- Main page --- */
export default function Wardrobe() {
  // Wardrobe items
  const [garments, setGarments] = useState([]);
  const [accessories, setAccessories] = useState([]);
  const [artifacts, setArtifacts] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]); // paths selected for generation

  // Upload
  const [uploading, setUploading] = useState(false);
  const garmentInputRef = useRef(null);
  const accessoryInputRef = useRef(null);
  const artifactInputRef = useRef(null);

  // Generation
  const [status, setStatus] = useState("idle");
  const [generatedImage, setGeneratedImage] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [modalSrc, setModalSrc] = useState(null);
  const pollingRef = useRef(false);

  // Saved outfits
  const [savedOutfits, setSavedOutfits] = useState([]);

  // Reel refs
  const garmentsReelRef = useRef(null);
  const accessoriesReelRef = useRef(null);
  const artifactsReelRef = useRef(null);
  const outfitsReelRef = useRef(null);
  const scrollReel = (ref, dir) => {
    if (ref.current) ref.current.scrollBy({ left: dir * 200, behavior: "smooth" });
  };

  const headers = { Authorization: `Bearer ${getToken()}` };

  // Filter placeholders
  const filterPlaceholders = (items) =>
    (items || []).filter((i) => !i.name.includes(".emptyFolderPlaceholder"));

  // Load wardrobe on mount
  const loadWardrobe = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/wardrobe`, { headers });
      const d = await res.json();
      setGarments(filterPlaceholders(d.garments || []));
      setAccessories(filterPlaceholders(d.accessories || []));
      // Artifacts (independent objects)
      try {
        const artRes = await fetch(`${API_URL}/artifacts`, { headers });
        const artData = await artRes.json();
        setArtifacts(filterPlaceholders(artData.artifacts || []));
      } catch {}
      // Load saved outfits
      const outfitRes = await fetch(`${API_URL}/wardrobe/outfits`, { headers });
      if (outfitRes.ok) {
        const od = await outfitRes.json();
        setSavedOutfits(filterPlaceholders(od.outfits || []));
      }
    } catch {}
  }, []);

  useEffect(() => { loadWardrobe(); }, [loadWardrobe]);

  // Toggle selection of an item for generation
  const toggleSelect = (path) => {
    setSelectedItems((prev) =>
      prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path]
    );
  };

  // --- Resize image to fit within Vercel 4.5MB body limit ---
  const resizeImage = (file, maxDim = 2048) =>
    new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width <= maxDim && height <= maxDim && file.size < 3_500_000) {
          resolve(file);
          return;
        }
        const scale = Math.min(maxDim / width, maxDim / height, 1);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => resolve(blob), "image/png");
      };
      img.src = URL.createObjectURL(file);
    });

  // --- Upload garment ---
  const handleUploadGarment = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const resized = await resizeImage(file);
      const form = new FormData();
      form.append("file", resized, file.name);
      const res = await fetch(`${API_URL}/wardrobe/garments`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: form,
      });
      if (!res.ok) throw new Error("Error al subir prenda");
      await loadWardrobe();
    } catch (err) {
      setErrorMsg(err.message);
    }
    setUploading(false);
    if (garmentInputRef.current) garmentInputRef.current.value = "";
  };

  // --- Upload accessory ---
  const handleUploadAccessory = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const resized = await resizeImage(file);
      const form = new FormData();
      form.append("file", resized, file.name);
      const res = await fetch(`${API_URL}/wardrobe/accessories`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: form,
      });
      if (!res.ok) throw new Error("Error al subir complemento");
      await loadWardrobe();
    } catch (err) {
      setErrorMsg(err.message);
    }
    setUploading(false);
    if (accessoryInputRef.current) accessoryInputRef.current.value = "";
  };

  // --- Upload artifact ---
  const handleUploadArtifact = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const resized = await resizeImage(file);
      const form = new FormData();
      form.append("file", resized, file.name);
      const res = await fetch(`${API_URL}/artifacts`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: form,
      });
      if (!res.ok) throw new Error("Error al subir artefacto");
      await loadWardrobe();
    } catch (err) {
      setErrorMsg(err.message);
    }
    setUploading(false);
    if (artifactInputRef.current) artifactInputRef.current.value = "";
  };

  // --- Rename artifact ---
  const handleRenameArtifact = async (oldName, newName) => {
    const clean = newName.trim();
    if (!clean || clean === oldName.replace(".png", "").replace(/_/g, " ")) return;
    try {
      const res = await fetch(`${API_URL}/artifacts/${encodeURIComponent(oldName)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ name: clean.replace(/ /g, "_") }),
      });
      if (!res.ok) throw new Error("Error al renombrar");
      await loadWardrobe();
    } catch (err) {
      setErrorMsg(err.message);
    }
  };

  // --- Rename outfit ---
  const handleRenameOutfit = async (oldName, newName) => {
    const clean = newName.trim();
    if (!clean || clean === oldName.replace(".png", "").replace(/_/g, " ")) return;
    try {
      const res = await fetch(`${API_URL}/wardrobe/outfits/${encodeURIComponent(oldName)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ name: clean.replace(/ /g, "_") }),
      });
      if (!res.ok) throw new Error("Error al renombrar");
      await loadWardrobe();
    } catch (err) {
      setErrorMsg(err.message);
    }
  };

  // --- Delete wardrobe or artifact item ---
  const handleDelete = async (path) => {
    try {
      const endpoint = path.startsWith("artifacts/")
        ? `${API_URL}/artifacts/asset?path=${encodeURIComponent(path)}`
        : `${API_URL}/wardrobe/asset?path=${encodeURIComponent(path)}`;
      await fetch(endpoint, { method: "DELETE", headers });
      setSelectedItems((prev) => prev.filter((p) => p !== path));
      await loadWardrobe();
    } catch (err) {
      setErrorMsg(err.message);
    }
  };

  // --- Generate outfit variations ---
  const canGenerate = selectedItems.length > 0 && status !== "generating";

  const pollStatus = useCallback(async (jid) => {
    pollingRef.current = true;
    for (let i = 0; i < 120; i++) {
      if (!pollingRef.current) return;
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const res = await fetch(
          `${API_URL}/wardrobe/${encodeURIComponent(jid)}/status`,
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
        const data = await res.json();
        if (data.status === "completed") {
          const resultRes = await fetch(
            `${API_URL}/wardrobe/${encodeURIComponent(jid)}/result`,
            { headers: { Authorization: `Bearer ${getToken()}` } }
          );
          const result = await resultRes.json();
          setGeneratedImage(result.image || null);
          setStatus("done");
          pollingRef.current = false;
          return;
        }
        if (data.status === "error") {
          setErrorMsg(data.error || "Error al generar");
          setStatus("error");
          pollingRef.current = false;
          return;
        }
      } catch (err) {
        console.error("Poll error:", err);
      }
    }
    setErrorMsg("Tiempo agotado");
    setStatus("error");
    pollingRef.current = false;
  }, []);

  const handleGenerate = async () => {
    setStatus("generating");
    setGeneratedImage(null);
    setErrorMsg("");
    try {
      const res = await fetch(`${API_URL}/wardrobe/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ item_paths: selectedItems }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || `Error ${res.status}`);
      }
      const data = await res.json();
      pollStatus(data.job_id);
    } catch (err) {
      setErrorMsg(err.message || "Error al enviar");
      setStatus("error");
    }
  };

  // --- Save generated outfit ---
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    if (!generatedImage) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/wardrobe/save`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ image_b64: generatedImage }),
      });
      if (res.ok) {
        await loadWardrobe();
      }
    } catch {}
    setSaving(false);
  };

  // --- Split into individual garments ---
  const [splitting, setSplitting] = useState(false);
  const handleSplit = async () => {
    if (!generatedImage) return;
    setSplitting(true);
    try {
      const res = await fetch(`${API_URL}/wardrobe/split`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ image_b64: generatedImage }),
      });
      if (res.ok) {
        await loadWardrobe();
      }
    } catch {}
    setSplitting(false);
  };

  // --- Load saved outfit into result ---
  const loadOutfitToResult = async (url) => {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const reader = new FileReader();
      reader.onloadend = () => {
        const b64 = reader.result.split(",")[1];
        setGeneratedImage(b64);
        setStatus("done");
      };
      reader.readAsDataURL(blob);
    } catch {}
  };

  const downloadImage = (b64) => {
    const link = document.createElement("a");
    link.href = b64.startsWith("data:") ? b64 : `data:image/png;base64,${b64}`;
    link.download = `wardrobe_outfit.png`;
    link.click();
  };

  const getB64Src = (b64) => {
    if (!b64) return null;
    return b64.startsWith("data:") ? b64 : `data:image/png;base64,${b64}`;
  };

  // Build path for item
  const garmentPath = (item) => `wardrobe/garments/${item.name}`;
  const accessoryPath = (item) => `wardrobe/accessories/${item.name}`;
  const artifactPath = (item) => `artifacts/${item.name}`;

  return (
    <div className="wd-page">
      {/* LEFT — Wardrobe management */}
      <div className="cs-panel cs-panel--left wd-panel--left">
        <div className="cs-result-header">
          <h2 className="cs-panel__title">Wardrobe</h2>
          <div className="wd-header-actions">
            <span className="wd-selected-count">
              {selectedItems.length > 0 && `${selectedItems.length} seleccionados`}
            </span>
            <button
              className={`cs-btn-generate ${status === "generating" ? "cs-btn-generate--loading" : ""}`}
              disabled={!canGenerate}
              onClick={handleGenerate}
            >
              {status === "generating" ? "Generando..." : "Generar Variaciones"}
            </button>
          </div>
        </div>

        {/* Garments section */}
        <section className="cs-section">
          <div className="wd-section-header">
            <span className="cs-section__title">Prendas</span>
            <button
              className="wd-btn-upload"
              onClick={() => garmentInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? "Subiendo..." : "+ Subir prenda"}
            </button>
            <input
              ref={garmentInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleUploadGarment}
            />
          </div>
          <div className="cs-reel cs-reel--tall">
            <button className="cs-reel__btn" onClick={() => scrollReel(garmentsReelRef, -1)}>{"\u25C0"}</button>
            <div className="cs-reel__frames" ref={garmentsReelRef}>
              {garments.length > 0 ? (
                garments.map((item) => {
                  const path = garmentPath(item);
                  const isSelected = selectedItems.includes(path);
                  return (
                    <div
                      key={item.name}
                      className={`cs-reel-item cs-reel-item--wide wd-ref-item ${isSelected ? "wd-ref-item--selected" : ""}`}
                      onClick={() => toggleSelect(path)}
                    >
                      <img src={item.url} alt={item.name} onDoubleClick={(e) => { e.stopPropagation(); setModalSrc(item.url); }} />
                      <button
                        className="wd-ref-item__delete"
                        onClick={(e) => { e.stopPropagation(); handleDelete(path); }}
                        title="Eliminar"
                      >&times;</button>
                      {isSelected && <span className="wd-ref-item__check">&#10003;</span>}
                      <span className="cs-reel-item__name">{item.name.replace(".png", "").replace(/_/g, " ")}</span>
                    </div>
                  );
                })
              ) : (
                <span className="cs-reel-empty">Sube prendas de referencia</span>
              )}
            </div>
            <button className="cs-reel__btn" onClick={() => scrollReel(garmentsReelRef, 1)}>{"\u25B6"}</button>
          </div>
        </section>

        {/* Accessories section */}
        <section className="cs-section">
          <div className="wd-section-header">
            <span className="cs-section__title">Complementos</span>
            <button
              className="wd-btn-upload"
              onClick={() => accessoryInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? "Subiendo..." : "+ Subir complemento"}
            </button>
            <input
              ref={accessoryInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleUploadAccessory}
            />
          </div>
          <div className="cs-reel cs-reel--tall">
            <button className="cs-reel__btn" onClick={() => scrollReel(accessoriesReelRef, -1)}>{"\u25C0"}</button>
            <div className="cs-reel__frames" ref={accessoriesReelRef}>
              {accessories.length > 0 ? (
                accessories.map((item) => {
                  const path = accessoryPath(item);
                  const isSelected = selectedItems.includes(path);
                  return (
                    <div
                      key={item.name}
                      className={`cs-reel-item cs-reel-item--wide wd-ref-item ${isSelected ? "wd-ref-item--selected" : ""}`}
                      onClick={() => toggleSelect(path)}
                    >
                      <img src={item.url} alt={item.name} onDoubleClick={(e) => { e.stopPropagation(); setModalSrc(item.url); }} />
                      <button
                        className="wd-ref-item__delete"
                        onClick={(e) => { e.stopPropagation(); handleDelete(path); }}
                        title="Eliminar"
                      >&times;</button>
                      {isSelected && <span className="wd-ref-item__check">&#10003;</span>}
                      <span className="cs-reel-item__name">{item.name.replace(".png", "").replace(/_/g, " ")}</span>
                    </div>
                  );
                })
              ) : (
                <span className="cs-reel-empty">Sube complementos de referencia</span>
              )}
            </div>
            <button className="cs-reel__btn" onClick={() => scrollReel(accessoriesReelRef, 1)}>{"\u25B6"}</button>
          </div>
        </section>

        {/* Artifacts section — read only */}
        <section className="cs-section">
          <div className="wd-section-header">
            <span className="cs-section__title">Artefactos</span>
            <button
              className="wd-btn-upload"
              onClick={() => artifactInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? "Subiendo..." : "+ Subir artefacto"}
            </button>
            <input
              ref={artifactInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleUploadArtifact}
            />
          </div>
          <div className="cs-reel cs-reel--tall">
            <button className="cs-reel__btn" onClick={() => scrollReel(artifactsReelRef, -1)}>{"\u25C0"}</button>
            <div className="cs-reel__frames" ref={artifactsReelRef}>
              {artifacts.length > 0 ? (
                artifacts.map((item) => {
                  const path = artifactPath(item);
                  const isSelected = selectedItems.includes(path);
                  return (
                    <div
                      key={item.name}
                      className={`cs-reel-item cs-reel-item--wide wd-ref-item ${isSelected ? "wd-ref-item--selected" : ""}`}
                      onClick={() => toggleSelect(path)}
                    >
                      <img src={item.url} alt={item.name} onDoubleClick={(e) => { e.stopPropagation(); setModalSrc(item.url); }} />
                      <button
                        className="wd-ref-item__delete"
                        onClick={(e) => { e.stopPropagation(); handleDelete(path); }}
                        title="Eliminar"
                      >&times;</button>
                      {isSelected && <span className="wd-ref-item__check">&#10003;</span>}
                      <input
                        className="cs-reel-item__name wd-artifact-name-input"
                        defaultValue={item.name.replace(".png", "").replace(/_/g, " ")}
                        onClick={(e) => e.stopPropagation()}
                        onBlur={(e) => handleRenameArtifact(item.name, e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.target.blur(); } }}
                      />
                    </div>
                  );
                })
              ) : (
                <span className="cs-reel-empty">Sin artefactos</span>
              )}
            </div>
            <button className="cs-reel__btn" onClick={() => scrollReel(artifactsReelRef, 1)}>{"\u25B6"}</button>
          </div>
        </section>

        {/* Saved outfits */}
        <section className="cs-section">
          <span className="cs-section__title">Outfits generados</span>
          <div className="cs-reel">
            <button className="cs-reel__btn" onClick={() => scrollReel(outfitsReelRef, -1)}>{"\u25C0"}</button>
            <div className="cs-reel__frames" ref={outfitsReelRef}>
              {savedOutfits.length > 0 ? (
                savedOutfits.map((item) => (
                  <div key={item.name} className="cs-reel-item cs-reel-item--sheet" onClick={() => loadOutfitToResult(item.url)} title="Cargar en resultado">
                    <img src={item.url} alt={item.name} />
                    <input
                      className="cs-reel-item__name wd-artifact-name-input"
                      defaultValue={item.name.replace(".png", "").replace(/_/g, " ")}
                      onClick={(e) => e.stopPropagation()}
                      onBlur={(e) => handleRenameOutfit(item.name, e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.target.blur(); } }}
                    />
                  </div>
                ))
              ) : (
                <span className="cs-reel-empty">Genera variaciones de vestuario</span>
              )}
            </div>
            <button className="cs-reel__btn" onClick={() => scrollReel(outfitsReelRef, 1)}>{"\u25B6"}</button>
          </div>
        </section>

        {errorMsg && <div className="cs-error">{errorMsg}</div>}
      </div>

      {/* RIGHT — Result */}
      <div className="cs-panel cs-panel--right wd-panel--right">
        <h2 className="cs-panel__title">Resultado</h2>

        {status === "idle" && !generatedImage && (
          <div className="cs-results-empty">
            <p>Selecciona prendas/complementos y pulsa Generar Variaciones</p>
          </div>
        )}

        <div className="cs-results-grid">
          <div className="cs-result-card">
            <div
              className={`cs-result-card__image ${generatedImage ? "cs-result-card__image--loaded" : ""}`}
              onClick={() => generatedImage && setModalSrc(getB64Src(generatedImage))}
            >
              {generatedImage ? (
                <img src={getB64Src(generatedImage)} alt="Outfit variations" />
              ) : (
                <div className="cs-result-card__placeholder">
                  {status === "generating" && <span className="cs-spinner" />}
                </div>
              )}
            </div>
            <div className="cs-result-card__footer">
              <span className="cs-result-card__label">Variaciones de vestuario</span>
              <div className="cs-result-card__actions">
                {generatedImage && (
                  <button
                    className="cs-btn-save"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? "Guardando..." : "Guardar"}
                  </button>
                )}
                {generatedImage && (
                  <button
                    className="wd-btn-split"
                    onClick={handleSplit}
                    disabled={splitting}
                    title="Cortar en prendas individuales y guardar"
                  >
                    {splitting ? "Cortando..." : "Split"}
                  </button>
                )}
                {generatedImage && (
                  <button
                    className="cs-result-card__download"
                    onClick={() => downloadImage(generatedImage)}
                  >
                    &#x2B73;
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {modalSrc && (
        <ImageModal
          src={modalSrc}
          label="Vista"
          onClose={() => setModalSrc(null)}
        />
      )}
    </div>
  );
}
